import React, { useState, useEffect } from 'react';
import { useApp } from '../App';
import { clearSavedToken, validateToken } from '../lib/tokens';
import useHashRoute from '../lib/useHashRoute';
import HouseInfoView from '../components/HouseInfoView';
import TabBar from '../components/ui/TabBar';
import ScheduleTab from './caregiver/ScheduleTab';
import SessionTab from './caregiver/SessionTab';

// ─── SHARED VIEW (caregiver surface) ──────────────────────────────────────────
// What a tokenized share link opens. No Google auth — the token doc's scopes
// decide which tabs exist. Reads only Firestore (published schedule snapshots,
// kids/house/emergency info, sessions); never Google Calendar.

export default function SharedView({ tokenDoc }) {
  const { familyData } = useApp() || {};
  const [expired, setExpired] = useState(false);

  const label  = tokenDoc?.label || '';
  const scopes = tokenDoc?.scopes || {};

  // Re-validate whenever the phone comes back to this tab — a revoked or
  // expired link should stop working on refocus, not just on next boot.
  useEffect(() => {
    async function recheck() {
      if (document.visibilityState !== 'visible' || !tokenDoc?.token) return;
      const doc = await validateToken(tokenDoc.token);
      if (!doc) {
        clearSavedToken();
        setExpired(true);
      }
    }
    document.addEventListener('visibilitychange', recheck);
    return () => document.removeEventListener('visibilitychange', recheck);
  }, [tokenDoc?.token]);

  const TABS = [
    ...(scopes.schedule  ? [{ id: 'schedule',  label: '📅 Schedule' }] : []),
    ...(scopes.assignedEvents && tokenDoc?.caregiverId ? [{ id: 'assignedEvents', label: '🚗 My Assigned Events' }] : []),
    ...(scopes.kids      ? [{ id: 'kids',      label: '🌱 Kids' }] : []),
    ...(scopes.house     ? [{ id: 'house',     label: '🪵 House' }] : []),
    ...(scopes.emergency ? [{ id: 'emergency', label: '🚨 Emergency' }] : []),
    ...(scopes.sessions && tokenDoc?.caregiverId ? [{ id: 'sessions', label: '🌤️ My Sessions' }] : []),
  ];

  const [tab, navigate] = useHashRoute(TABS[0]?.id || 'schedule');
  const activeTab = TABS.some(t => t.id === tab) ? tab : (TABS[0]?.id || null);

  function handleLeave() {
    clearSavedToken();
    window.location.href = window.location.pathname; // drop hash, reload clean
  }

  if (expired) return (
    <div className="loading-screen" style={{ gap: 12, textAlign: 'center', padding: 24 }}>
      <div style={{ fontSize: 40 }}>🍂</div>
      <h2 style={{ color: 'var(--forest-dark)', margin: 0 }}>This link has expired</h2>
      <p style={{ fontSize: 14, color: 'var(--forest)', maxWidth: 320 }}>
        Ask the family to send you a fresh link from Settings → Caregivers.
      </p>
    </div>
  );

  if (!familyData) return (
    <div className="loading-screen">
      <div className="loading-spinner" />
      <p style={{ fontSize: 14, color: 'var(--sage)' }}>Loading…</p>
    </div>
  );

  return (
    <div className="module-screen guest-screen">
      <SharedHeader familyName={familyData.familyName || 'Family'} label={label} onLeave={handleLeave} />
      {label && <div style={{ padding: '6px 20px 0', fontSize: 15, color: 'var(--forest)' }}>Hi {label} 👋</div>}

      <TabBar tabs={TABS} value={activeTab} onChange={navigate} />

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
        {activeTab === 'schedule'  && <ScheduleTab familyData={familyData} />}
        {activeTab === 'assignedEvents' && <ScheduleTab familyData={familyData} onlyAssignedTo={tokenDoc?.caregiverId} />}
        {activeTab === 'kids'      && <HouseInfoView familyData={familyData} fixedSection="kids" hideInsurance />}
        {activeTab === 'house'     && <HouseInfoView familyData={familyData} fixedSection="house" />}
        {activeTab === 'emergency' && <HouseInfoView familyData={familyData} fixedSection="emergency" />}
        {activeTab === 'sessions'  && <SessionTab familyData={familyData} caregiverId={tokenDoc?.caregiverId} />}
        {!TABS.length && (
          <p style={{ padding: '24px 0', fontSize: 14, color: 'var(--forest)' }}>
            This link doesn't unlock any sections — ask the family to update it.
          </p>
        )}
      </div>
    </div>
  );
}

function SharedHeader({ familyName, label, onLeave }) {
  const [showLeave, setShowLeave] = useState(false);
  return (
    <div className="module-header">
      <div>
        <span className="home-header__title">{familyName}</span>
        {label && (
          <span style={{ fontSize: 12, color: 'var(--forest)', marginLeft: 8 }}>{label}</span>
        )}
      </div>
      <div style={{ marginLeft: 'auto', position: 'relative' }}>
        <button className="btn btn--sm leave-access-btn" aria-label="Options"
          onClick={() => setShowLeave(v => !v)}>···</button>
        {showLeave && (
          <div className="account-menu" onClick={() => setShowLeave(false)}>
            <button onClick={onLeave}>Leave / clear access</button>
          </div>
        )}
      </div>
    </div>
  );
}
